import { defineStore } from "pinia";
import { computed, ref } from "vue";
import type { GeoJSON } from "geojson";

import type { SelectId } from "./types";

// The one selection a workbench has, in three channels.
//
// They are the operator's state and never a server response: `record` is the one
// object the details pane is reading, `multi` is the set checked or swept up for
// bulk work, and `area` is the ring in force — the subject of "select all
// matching" and the area clause of a ledger filter. Keeping them in one store is
// what makes a map click and a ledger row click the same act: both call
// `select`, both read `record`/`multi` back, and no page keeps a parallel pair of
// refs that can disagree.
//
// A record and a multi-selection are independent. Clicking sets both (the one
// row is now the selection), while an additive click leaves the record alone and
// only grows the set — the gesture the map and the ledger both spell
// `additive`.

/** The stable key a multi-selection holds: the kind is part of it because a
 * Lead and a Signal can carry the same id, and it stays a string so a cell's
 * hex index and a row's number cannot collide as keys. This is the one place a
 * key is built. */
function keyOf(kind: string, id: SelectId): string {
	return `${kind}:${id}`;
}

export const useWorkbenchSelection = defineStore("workbench-selection", () => {
	/** The one object the details pane reads. The id is whatever the surface's
	 * `idOf` named — a numeric row id for most workbenches, a cell index for an
	 * H3 grid — and is only ever compared or keyed, never arithmetically. */
	const record = ref<{ kind: string; id: SelectId } | null>(null);
	const multi = ref<Set<string>>(new Set());
	const area = ref<GeoJSON.Polygon | null>(null);

	/** How many objects the multi-selection holds: what a bulk action posts and
	 * what the filter bar's area clause counts. */
	const count = computed(() => multi.value.size);

	/**
	 * The object a click landed on. A plain click replaces the selection with
	 * that one object; an additive one adds it to what is already held and
	 * leaves the rest alone. Either way the record follows the click, because
	 * the details pane should read what the operator last pointed at.
	 */
	function select(
		kind: string,
		id: SelectId,
		opts?: { additive?: boolean },
	): void {
		const key = keyOf(kind, id);
		record.value = { kind, id };
		if (opts?.additive) multi.value.add(key);
		else multi.value = new Set([key]);
	}

	/** Flip one object's membership — the ledger's checkbox. Deselecting the
	 * last object leaves nothing for the pane to read, so the record goes with
	 * it. */
	function toggle(kind: string, id: SelectId): void {
		const key = keyOf(kind, id);
		if (multi.value.delete(key)) {
			if (multi.value.size === 0) record.value = null;
			return;
		}
		multi.value.add(key);
	}

	function has(kind: string, id: SelectId): boolean {
		return multi.value.has(keyOf(kind, id));
	}

	/**
	 * An area and the selection it produced travel together: the ledger shows
	 * the multi-selection and the filter bar names the ring, and the two must
	 * never describe different sets.
	 */
	function setArea(ring: [number, number][], keys: Iterable<string>): void {
		area.value = {
			type: "Polygon",
			coordinates: [ring.map(([lng, lat]) => [lng, lat])],
		};
		multi.value = new Set(keys);
	}

	function clear(): void {
		record.value = null;
		multi.value = new Set();
		area.value = null;
	}

	return {
		record,
		multi,
		area,
		count,
		select,
		toggle,
		has,
		setArea,
		clear,
		// The store is a pinia singleton, so selection never leaks from one
		// workbench into another if each calls this on setup. It is the same act
		// as `clear`, under the name the mount-time call reads better as.
		reset: clear,
	};
});
